/**
 * User Management Service
 * Handles user CRUD operations, role assignments, and organization memberships
 */

import { apiClient } from './api-client';
import {
  EnhancedUser,
  UserOrganizationMembership,
  EnhancedUserRole,
  EnhancedPermission,
  UserInvitation,
  CreateInvitationRequest,
  AcceptInvitationRequest,
  AuditLogEntry,
  AuditLogQuery,
  RBACService
} from './rbac-types';
import { ApiResponse, Pagination } from '../../../contracts/types';

// ============================================================================
// REQUEST/RESPONSE TYPES
// ============================================================================

export interface CreateUserRequest {
  email: string;
  first_name: string;
  last_name: string;
  role: EnhancedUserRole;
  org_id: string;
  custom_permissions?: EnhancedPermission[];
  send_invitation?: boolean;
  temporary_access?: boolean;
  expires_at?: string;
}

export interface UpdateUserRequest {
  first_name?: string;
  last_name?: string;
  display_name?: string;
  timezone?: string;
  language?: string;
  phone?: string;
  department?: string;
  title?: string;
}

export interface UpdateUserRoleRequest {
  user_id: string;
  org_id: string;
  role: EnhancedUserRole;
  custom_permissions?: EnhancedPermission[];
}

export interface UsersListRequest {
  org_id?: string;
  role?: EnhancedUserRole;
  status?: 'active' | 'suspended' | 'pending_activation';
  search?: string;
  limit?: number;
  offset?: number;
  sort_by?: 'name' | 'email' | 'created_at' | 'last_login';
  sort_order?: 'asc' | 'desc';
}

export interface UsersListResponse {
  users: EnhancedUser[];
  pagination: Pagination;
  total_by_role: Record<EnhancedUserRole, number>;
}

export interface UserInvitationsListRequest {
  org_id?: string;
  status?: 'pending' | 'accepted' | 'expired' | 'revoked';
  limit?: number;
  offset?: number;
}

export interface UserInvitationsListResponse {
  invitations: UserInvitation[];
  pagination: Pagination;
}

export interface AuditLogResponse {
  entries: AuditLogEntry[];
  pagination: Pagination;
}

// ============================================================================
// USER MANAGEMENT SERVICE
// ============================================================================

export class UserManagementService {
  /**
   * Get list of users with filtering and pagination
   */
  async getUsers(params: UsersListRequest = {}): Promise<UsersListResponse> {
    try {
      const response = await apiClient.get<UsersListResponse>('/api/v1/users', {
        params,
        cacheKey: `users-${JSON.stringify(params)}`,
        cacheTTL: 2 * 60 * 1000, // 2 minutes
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch users');
      }

      return response.data!;
    } catch (error) {
      console.warn('Users API not available, using mock data:', error);
      return this.generateMockUsersResponse(params);
    }
  }

  /**
   * Get detailed user information by ID
   */
  async getUser(userId: string): Promise<EnhancedUser> {
    try {
      const response = await apiClient.get<EnhancedUser>(`/api/v1/users/${userId}`, {
        cacheKey: `user-${userId}`,
        cacheTTL: 5 * 60 * 1000, // 5 minutes
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch user');
      }

      return response.data!;
    } catch (error) {
      console.warn('User API not available, using mock data:', error);
      return this.generateMockUser(userId);
    }
  }

  /**
   * Create a new user
   */
  async createUser(request: CreateUserRequest): Promise<EnhancedUser> {
    const response = await apiClient.post<EnhancedUser>('/api/v1/users', request);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to create user');
    }

    return response.data!;
  }

  /**
   * Update user information
   */
  async updateUser(userId: string, request: UpdateUserRequest): Promise<EnhancedUser> {
    const response = await apiClient.patch<EnhancedUser>(`/api/v1/users/${userId}`, request);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update user');
    }

    return response.data!;
  }

  /**
   * Update user role and permissions in an organization
   */
  async updateUserRole(request: UpdateUserRoleRequest): Promise<UserOrganizationMembership> {
    const response = await apiClient.patch<UserOrganizationMembership>(
      `/api/v1/users/${request.user_id}/organizations/${request.org_id}/role`,
      {
        role: request.role,
        custom_permissions: request.custom_permissions
      }
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update user role');
    }

    return response.data!;
  }

  /**
   * Suspend a user
   */
  async suspendUser(userId: string, orgId: string, reason: string): Promise<void> {
    const response = await apiClient.post(
      `/api/v1/users/${userId}/organizations/${orgId}/suspend`,
      { reason }
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to suspend user');
    }
  }

  /**
   * Activate a suspended user
   */
  async activateUser(userId: string, orgId: string): Promise<void> {
    const response = await apiClient.post(
      `/api/v1/users/${userId}/organizations/${orgId}/activate`
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to activate user');
    }
  }

  /**
   * Remove user from organization
   */
  async removeUserFromOrganization(userId: string, orgId: string): Promise<void> {
    const response = await apiClient.delete(
      `/api/v1/users/${userId}/organizations/${orgId}`
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to remove user from organization');
    }
  }

  // ============================================================================
  // USER INVITATION SYSTEM
  // ============================================================================

  /**
   * Send user invitation
   */
  async sendInvitation(request: CreateInvitationRequest): Promise<UserInvitation> {
    const response = await apiClient.post<UserInvitation>('/api/v1/invitations', request);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to send invitation');
    }

    return response.data!;
  }

  /**
   * Get list of invitations
   */
  async getInvitations(params: UserInvitationsListRequest = {}): Promise<UserInvitationsListResponse> {
    try {
      const response = await apiClient.get<UserInvitationsListResponse>('/api/v1/invitations', {
        params,
        cacheKey: `invitations-${JSON.stringify(params)}`,
        cacheTTL: 60 * 1000, // 1 minute
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch invitations');
      }

      return response.data!;
    } catch (error) {
      console.warn('Invitations API not available, using mock data:', error);
      return this.generateMockInvitationsResponse(params);
    }
  }

  /**
   * Accept invitation and create account
   */
  async acceptInvitation(request: AcceptInvitationRequest): Promise<EnhancedUser> {
    const response = await apiClient.post<EnhancedUser>('/api/v1/invitations/accept', request);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to accept invitation');
    }

    return response.data!;
  }

  /**
   * Resend invitation
   */
  async resendInvitation(invitationId: string): Promise<UserInvitation> {
    const response = await apiClient.post<UserInvitation>(`/api/v1/invitations/${invitationId}/resend`);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to resend invitation');
    }

    return response.data!;
  }

  /**
   * Revoke invitation
   */
  async revokeInvitation(invitationId: string): Promise<void> {
    const response = await apiClient.delete(`/api/v1/invitations/${invitationId}`);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to revoke invitation');
    }
  }

  // ============================================================================
  // AUDIT LOGGING
  // ============================================================================

  /**
   * Get audit log entries
   */
  async getAuditLog(params: AuditLogQuery = {}): Promise<AuditLogResponse> {
    try {
      const response = await apiClient.get<AuditLogResponse>('/api/v1/audit-log', {
        params,
        cacheKey: `audit-log-${JSON.stringify(params)}`,
        cacheTTL: 30 * 1000, // 30 seconds
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch audit log');
      }

      return response.data!;
    } catch (error) {
      console.warn('Audit log API not available, using mock data:', error);
      return this.generateMockAuditLogResponse(params);
    }
  }

  /**
   * Log user action for audit trail
   */
  async logUserAction(action: string, resourceType: string, resourceId?: string, details?: Record<string, any>): Promise<void> {
    try {
      await apiClient.post('/api/v1/audit-log', {
        action,
        resource_type: resourceType,
        resource_id: resourceId,
        details,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to log user action:', error);
    }
  }

  // ============================================================================
  // PERMISSION UTILITIES
  // ============================================================================

  /**
   * Get available permissions for a role
   */
  getRolePermissions(role: EnhancedUserRole): EnhancedPermission[] {
    return RBACService.getUserPermissions({
      organization_memberships: [{ role, permissions: [] } as any]
    } as EnhancedUser, '');
  }

  /**
   * Check if a user can perform an action
   */
  async canUserPerformAction(
    userId: string,
    orgId: string,
    permission: EnhancedPermission
  ): Promise<boolean> {
    try {
      const user = await this.getUser(userId);
      return RBACService.hasPermission({ user, org_id: orgId }, permission);
    } catch (error) {
      console.error('Error checking user permissions:', error);
      return false;
    }
  }

  // ============================================================================
  // MOCK DATA GENERATORS
  // ============================================================================

  private generateMockUsersResponse(params: UsersListRequest): UsersListResponse {
    const mockUsers: EnhancedUser[] = [
      {
        user_id: 'user_001',
        email: 'admin@briefcasebrain.com',
        name: 'Admin User',
        global_role: 'super_admin',
        organization_memberships: [{
          membership_id: 'mem_001',
          user_id: 'user_001',
          org_id: 'org_abc123',
          role: 'org_admin',
          permissions: ['users:create', 'users:read', 'users:update', 'organization:admin'],
          status: 'active',
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        }],
        active_org_id: 'org_abc123',
        profile: {
          first_name: 'Admin',
          last_name: 'User',
          display_name: 'Admin',
          timezone: 'America/Los_Angeles',
          language: 'en',
          department: 'Engineering'
        },
        security: {
          mfa_enabled: true,
          mfa_methods: ['totp'],
          password_last_changed: '2024-01-01T00:00:00Z',
          last_login: '2024-11-24T12:00:00Z',
          login_count: 150,
          failed_login_attempts: 0,
          account_locked: false,
          password_reset_required: false,
          session_timeout_minutes: 480
        },
        preferences: {
          theme: 'light',
          notifications: {
            email_enabled: true,
            push_enabled: false,
            slack_enabled: true,
            notification_types: {
              experiment_completed: true,
              deployment_failed: true,
              cost_threshold_exceeded: true,
              accuracy_alert: true,
              system_maintenance: true,
              security_alert: true
            }
          },
          dashboard: {
            default_time_range: '24h',
            default_metrics: ['accuracy', 'latency_p95'],
            auto_refresh_enabled: true,
            auto_refresh_interval: 30,
            charts_per_page: 6,
            favorite_agent_groups: ['ag_legal_docs_001']
          },
          privacy: {
            activity_tracking: true,
            analytics_tracking: true,
            data_export_allowed: true
          }
        },
        created_at: '2024-01-01T00:00:00Z',
        last_login: '2024-11-24T12:00:00Z'
      }
    ];

    return {
      users: mockUsers,
      pagination: {
        limit: params.limit || 20,
        offset: params.offset || 0,
        total: mockUsers.length,
        has_more: false
      },
      total_by_role: {
        super_admin: 1,
        org_admin: 0,
        org_manager: 0,
        team_lead: 0,
        analyst: 0,
        viewer: 0,
        external_viewer: 0,
        api_user: 0
      }
    };
  }

  private generateMockUser(userId: string): EnhancedUser {
    return {
      user_id: userId,
      email: 'user@briefcasebrain.com',
      name: 'Mock User',
      global_role: 'analyst',
      organization_memberships: [],
      profile: {
        first_name: 'Mock',
        last_name: 'User',
        timezone: 'UTC',
        language: 'en'
      },
      security: {
        mfa_enabled: false,
        mfa_methods: [],
        password_last_changed: '2024-01-01T00:00:00Z',
        last_login: '2024-11-24T12:00:00Z',
        login_count: 1,
        failed_login_attempts: 0,
        account_locked: false,
        password_reset_required: false,
        session_timeout_minutes: 480
      },
      preferences: {
        theme: 'system',
        notifications: {
          email_enabled: true,
          push_enabled: false,
          slack_enabled: false,
          notification_types: {
            experiment_completed: true,
            deployment_failed: true,
            cost_threshold_exceeded: false,
            accuracy_alert: true,
            system_maintenance: false,
            security_alert: true
          }
        },
        dashboard: {
          default_time_range: '1h',
          default_metrics: ['accuracy'],
          auto_refresh_enabled: false,
          auto_refresh_interval: 60,
          charts_per_page: 4,
          favorite_agent_groups: []
        },
        privacy: {
          activity_tracking: true,
          analytics_tracking: false,
          data_export_allowed: true
        }
      },
      created_at: '2024-01-01T00:00:00Z'
    };
  }

  private generateMockInvitationsResponse(params: UserInvitationsListRequest): UserInvitationsListResponse {
    const mockInvitations: UserInvitation[] = [
      {
        invitation_id: 'inv_001',
        email: 'newuser@company.com',
        org_id: 'org_abc123',
        role: 'analyst',
        permissions: ['metrics:read', 'experiments:read'],
        invited_by: 'user_001',
        created_at: '2024-11-20T10:00:00Z',
        expires_at: '2024-12-20T10:00:00Z',
        status: 'pending',
        welcome_message: 'Welcome to our analytics team!'
      }
    ];

    return {
      invitations: mockInvitations,
      pagination: {
        limit: params.limit || 20,
        offset: params.offset || 0,
        total: mockInvitations.length,
        has_more: false
      }
    };
  }

  private generateMockAuditLogResponse(params: AuditLogQuery): AuditLogResponse {
    const mockEntries: AuditLogEntry[] = [
      {
        id: 'audit_001',
        user_id: 'user_001',
        user_email: 'admin@briefcasebrain.com',
        org_id: 'org_abc123',
        action: 'user:create',
        resource_type: 'user',
        resource_id: 'user_002',
        details: { role: 'analyst', email: 'newuser@company.com' },
        ip_address: '192.168.1.100',
        user_agent: 'Mozilla/5.0...',
        timestamp: '2024-11-24T12:00:00Z',
        success: true
      }
    ];

    return {
      entries: mockEntries,
      pagination: {
        limit: params.limit || 50,
        offset: params.offset || 0,
        total: mockEntries.length,
        has_more: false
      }
    };
  }
}

// Singleton instance
export const userManagementService = new UserManagementService();

export default userManagementService;